import type { ProactiveGreetingPhrase, ProactiveGreetingResponse } from '@teo/widget-shared';
import {
  filterProactiveGreetingPhrases,
  GREETING_POPUP_MAX_WAIT_MS,
} from '@teo/widget-shared';
import { ensureAnalyticsVisitorId } from '../analytics/resolve-analytics-visitor-id.js';
import { flushWidgetAnalytics } from '../analytics';
import {
  buildProactiveGreetingRequest,
  fetchProactiveGreeting,
  greetingResponseToDisplayText,
  type FetchProactiveGreetingOptions,
} from '../api/fetch-proactive-greeting.js';
import { logPopup } from '../popups/popup-logger.js';
import { getWidgetSession } from '../session/widget-session.js';
import { computeGreetingId } from './pending-greeting-offer.js';

export type ProactiveGreetingCacheStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'aborted';

export type ProactiveGreetingCacheEntry = {
  key: string;
  pathname: string;
  slug: string;
  status: ProactiveGreetingCacheStatus;
  response: ProactiveGreetingResponse | null;
  /** Phrases for chat welcome + `history` on first send. */
  greetingPhrases: ProactiveGreetingPhrase[];
  /** From API or client-computed — idempotent commit key. */
  greetingId: string | null;
  /** Popup delay from greeting API (seconds), when provided. */
  delaySeconds: number | null;
  /** Joined text for popup bubble. */
  displayText: string | null;
  shouldShow: boolean;
  error: string | null;
  fetchedAt: number | null;
};

export type PrefetchProactiveGreetingOptions = {
  apiBase: string;
  slug: string;
  locale: string;
  mock?: boolean;
  url?: string;
  /** When false, greeting request uses URL heuristics only. Default true. */
  pageContextEnabled?: boolean;
  /** Flush analytics queue before greeting — default true. */
  flushEventsFirst?: boolean;
  /** Pass through to `/greeting` when chips are enabled. */
  quickReplyCount?: 1 | 2 | 3;
  fetchOptions?: FetchProactiveGreetingOptions;
};

export type WaitForProactiveGreetingResult = {
  entry: ProactiveGreetingCacheEntry | null;
  timedOut: boolean;
};

const cache = new Map<string, ProactiveGreetingCacheEntry>();
const inflight = new Map<string, Promise<ProactiveGreetingCacheEntry>>();
const abortControllers = new Map<string, AbortController>();
const listeners = new Set<() => void>();

function cacheKey(slug: string, pathname: string): string {
  return `${slug}:${pathname}`;
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  return error instanceof Error && error.name === 'AbortError';
}

function notifyListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

function setEntry(entry: ProactiveGreetingCacheEntry): void {
  cache.set(entry.key, entry);
  notifyListeners();
}

export function subscribeProactiveGreeting(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getProactiveGreetingCache(
  slug: string,
  pathname: string,
): ProactiveGreetingCacheEntry | null {
  return cache.get(cacheKey(slug, pathname)) ?? null;
}

export function clearProactiveGreetingCache(): void {
  for (const controller of abortControllers.values()) {
    controller.abort();
  }
  abortControllers.clear();
  cache.clear();
  inflight.clear();
  notifyListeners();
}

/** Cancel an in-flight greeting fetch (e.g. on SPA navigation). */
export function abortProactiveGreetingPrefetch(
  slug: string,
  pathname: string,
): void {
  const key = cacheKey(slug, pathname);
  abortControllers.get(key)?.abort();
  abortControllers.delete(key);
}

export function waitForProactiveGreeting(
  slug: string,
  pathname: string,
  options?: { timeoutMs?: number },
): Promise<WaitForProactiveGreetingResult> {
  const key = cacheKey(slug, pathname);
  const existing = cache.get(key);
  // `aborted` is not terminal — a later schedule must wait for a new prefetch.
  if (
    existing &&
    existing.status !== 'loading' &&
    existing.status !== 'aborted'
  ) {
    return Promise.resolve({ entry: existing, timedOut: false });
  }

  const timeoutMs = options?.timeoutMs ?? GREETING_POPUP_MAX_WAIT_MS;

  return new Promise((resolve) => {
    const finish = (result: WaitForProactiveGreetingResult) => {
      clearTimeout(timer);
      unsub();
      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({
        entry: cache.get(key) ?? null,
        timedOut: true,
      });
    }, timeoutMs);

    const unsub = subscribeProactiveGreeting(() => {
      const entry = cache.get(key);
      if (
        entry &&
        entry.status !== 'loading' &&
        entry.status !== 'aborted'
      ) {
        finish({ entry, timedOut: false });
      }
    });

    const current = cache.get(key);
    if (
      current &&
      current.status !== 'loading' &&
      current.status !== 'aborted'
    ) {
      finish({ entry: current, timedOut: false });
    }
  });
}

function buildLoadingEntry(slug: string, pathname: string): ProactiveGreetingCacheEntry {
  return {
    key: cacheKey(slug, pathname),
    pathname,
    slug,
    status: 'loading',
    response: null,
    greetingPhrases: [],
    greetingId: null,
    delaySeconds: null,
    displayText: null,
    shouldShow: false,
    error: null,
    fetchedAt: null,
  };
}

function buildCacheEntryFromResponse(
  key: string,
  pathname: string,
  slug: string,
  response: ProactiveGreetingResponse,
): ProactiveGreetingCacheEntry {
  const greetingPhrases = filterProactiveGreetingPhrases(response.greeting);
  const displayText = greetingResponseToDisplayText(response);
  const delaySeconds =
    typeof response.delay_seconds === 'number' &&
    Number.isFinite(response.delay_seconds)
      ? response.delay_seconds
      : null;
  const fetchedAt = Date.now();
  const greetingId = computeGreetingId(slug, pathname, {
    apiGreetingId: response.greeting_id,
    fetchedAt,
    phraseCount: greetingPhrases.length,
  });

  return {
    key,
    pathname,
    slug,
    status: 'ready',
    response,
    greetingPhrases,
    greetingId,
    delaySeconds,
    displayText,
    shouldShow: Boolean(response.should_show && greetingPhrases.length > 0),
    error: null,
    fetchedAt,
  };
}

export async function prefetchProactiveGreeting(
  options: PrefetchProactiveGreetingOptions,
): Promise<ProactiveGreetingCacheEntry> {
  const pathname =
    options.url != null
      ? new URL(options.url).pathname
      : typeof window !== 'undefined'
        ? window.location.pathname
        : '/';
  const key = cacheKey(options.slug, pathname);
  const existing = cache.get(key);

  if (existing?.status === 'ready') {
    return existing;
  }

  abortProactiveGreetingPrefetch(options.slug, pathname);

  const controller = new AbortController();
  abortControllers.set(key, controller);

  const loadingEntry = buildLoadingEntry(options.slug, pathname);
  setEntry(loadingEntry);

  const task = (async (): Promise<ProactiveGreetingCacheEntry> => {
    try {
      if (options.flushEventsFirst !== false) {
        await flushWidgetAnalytics();
      }

      const visitorId = await ensureAnalyticsVisitorId();
      if (!visitorId) {
        throw new Error('visitor_id unavailable');
      }

      const session = getWidgetSession();
      const request = await buildProactiveGreetingRequest({
        url: options.url,
        visitorId,
        sessionId: session?.sessionId ?? null,
        locale: options.locale,
        pageContextEnabled: options.pageContextEnabled,
        quickReplyCount: options.quickReplyCount,
      });

      const response = await fetchProactiveGreeting(
        options.apiBase,
        options.slug,
        request,
        {
          mock: options.mock,
          signal: controller.signal,
          ...options.fetchOptions,
        },
      );

      const entry = buildCacheEntryFromResponse(
        key,
        pathname,
        options.slug,
        response,
      );
      const displayText = entry.displayText;
      logPopup('greeting-cache', 'ready', {
        pathname,
        shouldShow: entry.shouldShow,
        phraseCount: entry.greetingPhrases.length,
        delaySeconds: entry.delaySeconds,
        preview: displayText?.slice(0, 80),
        source: response.source,
      });
      setEntry(entry);
      return entry;
    } catch (error) {
      if (controller.signal.aborted || isAbortError(error)) {
        logPopup('greeting-cache', 'aborted', { pathname });
        const entry: ProactiveGreetingCacheEntry = {
          key,
          pathname,
          slug: options.slug,
          status: 'aborted',
          response: null,
          greetingPhrases: [],
          greetingId: null,
          delaySeconds: null,
          displayText: null,
          shouldShow: false,
          error: null,
          fetchedAt: Date.now(),
        };
        if (cache.get(key)?.status === 'loading') {
          cache.delete(key);
          notifyListeners();
        }
        return entry;
      }

      const entry: ProactiveGreetingCacheEntry = {
        key,
        pathname,
        slug: options.slug,
        status: 'error',
        response: null,
        greetingPhrases: [],
        greetingId: null,
        delaySeconds: null,
        displayText: null,
        shouldShow: false,
        error: error instanceof Error ? error.message : String(error),
        fetchedAt: Date.now(),
      };
      logPopup('greeting-cache', 'error', { pathname, error: entry.error }, 'warn');
      setEntry(entry);
      return entry;
    } finally {
      if (abortControllers.get(key) === controller) {
        abortControllers.delete(key);
      }
      inflight.delete(key);
    }
  })();

  inflight.set(key, task);
  return task;
}
